import React, { useState, useEffect } from 'react'
import { useAdminApi } from '../../contexts/AdminApiContext'
import { RefreshCw, AlertTriangle, Image, Music, FileText, File, FileJson } from 'lucide-react'

interface FileViewerProps {
  path: string
}

interface R2File {
  key: string
  size: number
  lastModified: string
  etag: string
  isDirectory?: boolean
}

const FileViewer: React.FC<FileViewerProps> = ({ path }) => {
  const [files, setFiles] = useState<R2File[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { apiService, isReady } = useAdminApi()

  useEffect(() => {
    if (isReady) {
      loadFiles()
    }
  }, [path, isReady])

  const loadFiles = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const result = await apiService.getR2Files(path)
      setFiles(result.files || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files')
      console.error('Error loading files:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString()
  }

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    switch (extension) {
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
        return <Image size={16} />
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'm4a':
        return <Music size={16} />
      case 'pdf':
        return <FileText size={16} />
      case 'txt':
      case 'md':
        return <FileText size={16} />
      case 'json':
        return <FileJson size={16} />
      default:
        return <File size={16} />
    }
  }

  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-text">
          <span className="loading-spinner"></span>
          Loading files from {path}...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-message">
        <span><AlertTriangle size={16} /></span>
        Error loading files: {error}
        <button 
          className="btn btn-secondary" 
          onClick={loadFiles}
          style={{ marginLeft: '1rem' }}
        >
          Retry
        </button>
      </div>
    )
  }

  if (!files || files.length === 0) {
    return (
      <div className="table-container">
        <div className="loading-text">
          No files found in {path}
        </div>
      </div>
    )
  }

  return (
    <div className="table-container">
      <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {files.length} file{files.length !== 1 ? 's' : ''} in {path}
          </span>
          <button 
            className="btn btn-secondary" 
            onClick={loadFiles}
            style={{ padding: '0.5rem 1rem', fontSize: '0.75rem' }}
          >
            <RefreshCw size={14} style={{ marginRight: '0.5rem' }} />
            Refresh
          </button>
        </div>
      </div>
      
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Last Modified</th>
              <th>ETag</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file, index) => (
              <tr key={index}>
                <td style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  maxWidth: '300px',
                  wordBreak: 'break-word'
                }}>
                  <span>{getFileIcon(file.key)}</span>
                  {file.key.replace(path, '')}
                </td>
                <td style={{ color: 'var(--text-muted)' }}>
                  {formatFileSize(file.size)}
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  {formatDate(file.lastModified)}
                </td>
                <td style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {file.etag}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default FileViewer